/* global google */

import getSVG from './svg'

const createOverlay = (bounds, overlaySVG, eventSVG) => {
  const overlay = new google.maps.OverlayView()

  /*
  The next 3 function definitions are instance methods of
  OverlayView and should not be bound.

  In other words:

  Do not change `draw`, `onRemove` and `onAdd` to an arrow function!
  */

  overlay.draw = function () {
    const projection = this.getProjection()

    const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest())
    const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast())

    overlaySVG.style.left = eventSVG.style.left = sw.x + 'px'
    overlaySVG.style.top = eventSVG.style.top = ne.y + 'px'
    overlaySVG.style.width = eventSVG.style.width = ne.x - sw.x + 'px'
    overlaySVG.style.height = eventSVG.style.height = sw.y - ne.y + 'px'
  }

  overlay.onRemove = function () {
    overlaySVG.parentNode.removeChild(overlaySVG)
    eventSVG.parentNode.removeChild(eventSVG)
  }

  overlay.onAdd = function () {
    const panes = this.getPanes()
    const overlayContainer = panes.overlayLayer
    const eventContainer = panes.overlayMouseTarget
    overlaySVG.style.position = eventSVG.style.position = 'absolute'
    overlayContainer.appendChild(overlaySVG)
    eventContainer.appendChild(eventSVG)
  }

  return overlay
}

const noop = () => {}
const ready = (obj) =>
  obj.contentDocument && obj.contentDocument.readyState == 'complete'

export default (map, bounds, regions, DEBUG = false) => {
  /**
   * Create the region overlay.
   */
  const handlers = {}
  handlers.over = noop
  handlers.out = noop
  handlers.click = noop

  /*
  Since we might be hiding regions before the SVG has loaded
  we'll be keeping track of the visiblity state of all regions.

  That way we can do the hiding as soon as the load event triggers.
  */
  const regionVisibility = Object.keys(regions).reduce((accumulator, key) => {
    accumulator[key] = true
    return accumulator
  }, {})

  const overlaySVG = getSVG(map.getDiv().ownerDocument)
  const eventSVG = getSVG(map.getDiv().ownerDocument)

  overlaySVG.onload = () =>
    Object.keys(regions).forEach((key) => {
      const region = regions[key]
      const overlayRegion = overlaySVG.contentDocument.getElementById(key)

      // overlayRegions have a fill and stroke
      overlayRegion.style.fill = region.fillColor
      overlayRegion.style.stroke = region.strokeColor
      overlayRegion.style.transition = 'fill-opacity .1s'
      overlayRegion.style.fillOpacity = 0.1

      if (regionVisibility[key] === false) {
        // Region was hidden before it was loaded
        overlayRegion.style.visibility = 'hidden'
      }
    })

  eventSVG.onload = () =>
    Object.keys(regions).forEach((key) => {
      const eventRegion = eventSVG.contentDocument.getElementById(key)

      // eventRegions are invisible, but turn the cursor into a pointer
      eventRegion.style.fill = eventRegion.style.stroke = 'transparent'
      eventRegion.style.cursor = 'pointer'

      if (regionVisibility[key] === false) {
        // Region was hidden before it was loaded
        eventRegion.style.pointer = 'default'
      }

      eventRegion.addEventListener('mouseover', () => {
        handlers.over(key)
      })

      eventRegion.addEventListener('mouseout', () => {
        handlers.out(key)
      })

      eventRegion.addEventListener('click', () => {
        handlers.click(key)
      })
    })

  // Add the overlay to the map.
  const overlay = createOverlay(bounds, overlaySVG, eventSVG)
  overlay.setMap(map)

  if (DEBUG) {
    /**
     * Draw a rectangle to show the bounding box.
     * To see if things are lining up correctly.
     */
    new google.maps.Rectangle({ map, bounds })
  }

  const regionOverlay = Object.keys(regions).reduce((accumulator, key) => {
    accumulator[key] = {
      show: () => {
        if (ready(overlaySVG)) {
          const el = overlaySVG.contentDocument.getElementById(key)
          if (el) el.style.visibility = 'visible'
        }
        if (ready(eventSVG)) {
          const el = eventSVG.contentDocument.getElementById(key)
          if (el) el.style.cursor = 'pointer'
        }
        regionVisibility[key] = true
      },
      hide: () => {
        if (ready(overlaySVG)) {
          const el = overlaySVG.contentDocument.getElementById(key)
          if (el) el.style.visibility = 'hidden'
        }
        if (ready(eventSVG)) {
          const el = eventSVG.contentDocument.getElementById(key)
          if (el) el.style.cursor = 'default'
        }
        regionVisibility[key] = false
      },
      focus: () => {
        Object.keys(regionOverlay).forEach((key) => regionOverlay[key].blur())
        if (ready(overlaySVG)) {
          const el = overlaySVG.contentDocument.getElementById(key)
          if (el) el.style.fillOpacity = 0.25
        }
      },
      blur: () => {
        if (ready(overlaySVG)) {
          const el = overlaySVG.contentDocument.getElementById(key)
          if (el) el.style.fillOpacity = 0.1
        }
      },
    }

    return accumulator
  }, {})

  return {
    overlay: regionOverlay,
    handlers,
  }
}
